// ═══════════════════════════════════════════════════════════════
// KOTCH - CARD DEFINITIONS
// 59 cards total: 13 each of Spearman/Archer/Rider, 15 Knights, 5 Spies
// ═══════════════════════════════════════════════════════════════

import type {
  CardDefinition,
  CardType,
  CardCategory,
  CardInstance,
  Faction,
  CardAbility,
} from "../../types/kotch";

// ─────────────────────────────────────────────────────────────────
// CARD TYPE DESCRIPTIONS
// ─────────────────────────────────────────────────────────────────

export const CARD_TYPE_INFO: Record<
  CardType,
  { name: string; description: string; beats: string; losesTo: string }
> = {
  spearman: {
    name: "Spearman",
    description: "Infantry soldiers wielding polearms. Strong against cavalry.",
    beats: "Rider",
    losesTo: "Archer",
  },
  archer: {
    name: "Archer",
    description: "Ranged fighters with bows. Deadly to infantry.",
    beats: "Spearman",
    losesTo: "Rider",
  },
  rider: {
    name: "Rider",
    description: "Mounted cavalry. Swift and powerful against ranged units.",
    beats: "Archer",
    losesTo: "Spearman",
  },
  knight: {
    name: "Knight",
    description:
      "Elite armored warriors. Defeat all basic troops, but vulnerable to sabotage.",
    beats: "All basic types",
    losesTo: "Spy",
  },
  spy: {
    name: "Spy",
    description:
      "Covert operatives. Can assassinate knights, but useless in open combat.",
    beats: "Knight",
    losesTo: "All basic types",
  },
};

// ─────────────────────────────────────────────────────────────────
// CARD GENERATION HELPERS
// ─────────────────────────────────────────────────────────────────

function createBasicCards(type: CardType): CardDefinition[] {
  const typeInfo = CARD_TYPE_INFO[type];
  return Array.from({ length: 13 }, (_, i) => ({
    id: `${type}_${i + 1}`,
    type,
    power: i + 1,
    category: "basic" as CardCategory,
    description: typeInfo.description,
  }));
}

// ─────────────────────────────────────────────────────────────────
// KNIGHT DEFINITIONS - Named knights with factions and abilities
// ─────────────────────────────────────────────────────────────────

interface KnightData {
  name: string;
  power: number;
  faction: Faction;
  ability: CardAbility;
}

const KNIGHTS: KnightData[] = [
  // MIGORA FACTION
  {
    name: "Mithra",
    power: 45,
    faction: "Migora",
    ability: {
      id: "unkillable",
      name: "Unkillable",
      trigger: "passive",
      description: "Beats Spies.",
      effect: "spy_immune",
    },
  },
  {
    name: "Reyaz",
    power: 25,
    faction: "Migora",
    ability: {
      id: "hero_of_grahir",
      name: "Hero of Grahir",
      trigger: "on_reveal",
      description: "If placed in Center, he fights in right flank too.",
      effect: "fight_center_and_right",
    },
  },
  {
    name: "Berjot",
    power: 22,
    faction: "Migora",
    ability: {
      id: "stalwart_defender",
      name: "Stalwart Defender",
      trigger: "passive",
      description: "Can only draw against higher power knights.",
      effect: "draw_vs_higher_knights",
    },
  },
  {
    name: "Hafler",
    power: 20,
    faction: "Migora",
    ability: {
      id: "hero_of_vara",
      name: "Hero of Vara",
      trigger: "on_reveal",
      description: "Summons a Warrior Giant (20 Power) either side of him.",
      effect: "summon_giant_20",
    },
  },
  {
    name: "Archralm Alon",
    power: 30,
    faction: "Migora",
    ability: {
      id: "archralm",
      name: "Archralm",
      trigger: "on_reveal",
      description: "Summons a Knight Ralm (15 Power) either side of him.",
      effect: "summon_ralm_15",
    },
  },

  // HYDARIA FACTION
  {
    name: "Armsmaster Lysiac",
    power: 25,
    faction: "Hydaria",
    ability: {
      id: "champion_of_hidas_dance",
      name: "Champion of Hida's Dance",
      trigger: "on_reveal",
      description:
        "Reveal and return any card placed opposite him to the owner's hand.", //Modified
      effect: "bounce_opposite",
    },
  },
  {
    name: "Battlemaster Lysiac",
    power: 30,
    faction: "Hydaria",
    ability: {
      id: "touched_by_magic",
      name: "Touched by Magic",
      trigger: "on_win",
      description:
        "Earns an extra point if the round he was played in was won.",
      effect: "extra_point_on_round_win",
    },
  },
  {
    name: "Ragos",
    power: 20,
    faction: "Hydaria",
    ability: {
      id: "high_warmaster",
      name: "High Warmaster",
      trigger: "on_lose",
      description: "Wins a round he is played in if he loses his lane.",
      effect: "win_round_on_lane_loss",
    },
  },

  // MOTRA FACTION
  {
    name: "Stone Sovereign Farkion",
    power: 35,
    faction: "Motra",
    ability: {
      id: "price_of_power",
      name: "Price of Power",
      trigger: "on_reveal",
      description: "Discards any non-special opponent cards played this round.",
      effect: "discard_opponent_basic",
    },
  },
  {
    name: "Councilor Farkion",
    power: 30,
    faction: "Motra",
    ability: {
      id: "coward",
      name: "Coward",
      trigger: "on_lose",
      description: "Returns a friendly card to hand if the round was lost.",
      effect: "return_friendly_on_loss",
    },
  },

  // RIGATA FACTION
  {
    name: "Arog",
    power: 30,
    faction: "Rigata",
    ability: {
      id: "royal_guard",
      name: "Royal Guard",
      trigger: "on_reveal",
      description: "Returns to hand if played with any Rigatan Royal.",
      effect: "return_with_royal",
    },
  },
  {
    name: "Renedil",
    power: 27,
    faction: "Rigata",
    ability: {
      id: "crown_prince",
      name: "Crown Prince",
      trigger: "on_reveal",
      description: "Plays Arog from the deck to his right.",
      effect: "summon_arog_right",
    },
  },
  {
    name: "Averil",
    power: 25,
    faction: "Rigata",
    ability: {
      id: "rigatan_royalty_averil",
      name: "Rigatan Royalty",
      trigger: "on_reveal",
      description: "Plays Quietus and Gimora from the deck.",
      effect: "summon_quietus_gimora",
    },
  },
  {
    name: "Quietus",
    power: 25,
    faction: "Rigata",
    ability: {
      id: "rigatan_royalty_quietus",
      name: "Rigatan Royalty",
      trigger: "on_reveal",
      description: "Plays Averil and Gimora from the deck.",
      effect: "summon_averil_gimora",
    },
  },
  {
    name: "Gimora",
    power: 25,
    faction: "Rigata",
    ability: {
      id: "rigatan_royalty_gimora",
      name: "Rigatan Royalty",
      trigger: "on_reveal",
      description: "Plays Averil and Quietus from the deck.",
      effect: "summon_averil_quietus",
    },
  },

  // VERA FACTION - The Free City-States
  {
    name: "Great Archon Veris",
    power: 30,
    faction: "Vera",
    ability: {
      id: "council_authority",
      name: "Council Authority",
      trigger: "passive",
      description: "Cannot be revealed by opponent effects.",
      effect: "reveal_immune",
    },
  },
  {
    name: "Polemarix Kira",
    power: 26,
    faction: "Vera",
    ability: {
      id: "war_council",
      name: "War Council",
      trigger: "on_win",
      description: "If she wins, reveal an additional card from opponent's hand.",
      effect: "reveal_extra_card",
    },
  },
  {
    name: "Magistrix Nira",
    power: 24,
    faction: "Vera",
    ability: {
      id: "political_insight",
      name: "Political Insight",
      trigger: "on_reveal",
      description: "Look at opponent's hand before they commit.",
      effect: "early_reveal",
    },
  },
  {
    name: "Polemarch Seren",
    power: 22,
    faction: "Vera",
    ability: {
      id: "tactical_maneuver",
      name: "Tactical Maneuver",
      trigger: "on_reveal",
      description: "May swap lanes with another friendly card.",
      effect: "swap_lanes",
    },
  },
  {
    name: "Archon Thane",
    power: 28,
    faction: "Vera",
    ability: {
      id: "veiled_council",
      name: "Veiled Council",
      trigger: "on_reveal",
      description: "Hide all friendly card powers this round.",
      effect: "hide_friendly_power",
    },
  },

  // MIGORA - Additional Knight
  {
    name: "Eleia",
    power: 10,
    faction: "Migora",
    ability: {
      id: "purge",
      name: "Purge",
      trigger: "on_reveal",
      description:
        "Removes any enemy regular cards (non special) played in lanes other than her own.",
      effect: "purge_other_lanes",
    },
  },
];

function createKnightCards(): CardDefinition[] {
  return KNIGHTS.map((knight, i) => ({
    id: `knight_${i + 1}`,
    type: "knight" as CardType,
    power: knight.power,
    category: "special" as CardCategory,
    name: knight.name,
    faction: knight.faction,
    ability: knight.ability,
    description: CARD_TYPE_INFO.knight.description,
  }));
}

// Named spies for flavor
const SPY_NAMES = ["Shadow", "Whisper", "Phantom", "Shade", "Ghost"];

function createSpyCards(): CardDefinition[] {
  return [1, 2, 3, 4, 5].map((power, i) => ({
    id: `spy_${i + 1}`,
    type: "spy" as CardType,
    power,
    category: "special" as CardCategory,
    name: SPY_NAMES[i],
    description: CARD_TYPE_INFO.spy.description,
  }));
}

// ─────────────────────────────────────────────────────────────────
// ALL CARD DEFINITIONS
// ─────────────────────────────────────────────────────────────────

const ALL_CARDS: CardDefinition[] = [
  ...createBasicCards("spearman"),
  ...createBasicCards("archer"),
  ...createBasicCards("rider"),
  ...createKnightCards(),
  ...createSpyCards(),
];

// Map for quick lookup by ID
export const CARD_DEFINITIONS: Record<string, CardDefinition> = {};
ALL_CARDS.forEach((card) => {
  CARD_DEFINITIONS[card.id] = card;
});

// ─────────────────────────────────────────────────────────────────
// CARD LOOKUP FUNCTIONS
// ─────────────────────────────────────────────────────────────────

export function getCardDefinition(
  definitionId: string
): CardDefinition | undefined {
  return CARD_DEFINITIONS[definitionId];
}

export function getCardFromInstance(
  instance: CardInstance
): CardDefinition | undefined {
  return CARD_DEFINITIONS[instance.definitionId];
}

export function getCardDisplayName(card: CardDefinition): string {
  if (card.name) {
    return `${card.name} (${card.type})`;
  }
  return `${CARD_TYPE_INFO[card.type].name} ${card.power}`;
}

export function getCardShortName(card: CardDefinition): string {
  if (card.name) {
    return card.name;
  }
  const typeAbbrev = {
    spearman: "S",
    archer: "A",
    rider: "R",
    knight: "K",
    spy: "Sp",
  };
  return `${typeAbbrev[card.type]}${card.power}`;
}

// ─────────────────────────────────────────────────────────────────
// DECK STATISTICS
// ─────────────────────────────────────────────────────────────────

export const DECK_STATS = {
  totalCards: 65,
  basicCards: 39,
  specialCards: 26,
  cardsByType: {
    spearman: 13,
    archer: 13,
    rider: 13,
    knight: 21,  // 15 original + 5 Vera + 1 Eleia
    spy: 5,
  },
  powerRange: {
    basic: { min: 1, max: 13 },
    knight: { min: 20, max: 45 },
    spy: { min: 1, max: 5 },
  },
};

// ─────────────────────────────────────────────────────────────────
// COMBAT TRIANGLE
// ─────────────────────────────────────────────────────────────────

// What each basic type beats
export const COMBAT_TRIANGLE: Record<string, CardType> = {
  spearman: "rider", // Spearman beats Rider
  rider: "archer", // Rider beats Archer
  archer: "spearman", // Archer beats Spearman
};

// Reverse lookup: what beats this type
export const BEATEN_BY: Record<string, CardType> = {
  spearman: "archer",
  archer: "rider",
  rider: "spearman",
};

export function getTypeAdvantage(
  type1: CardType,
  type2: CardType
): "wins" | "loses" | "neutral" {
  if (type1 === type2) return "neutral";

  // Check combat triangle for basic types
  if (COMBAT_TRIANGLE[type1] === type2) return "wins";
  if (COMBAT_TRIANGLE[type2] === type1) return "loses";

  return "neutral";
}
